/**
 * O protocolo, o apoio e o envio no material da mesa (E91.F2, GUS-51 RF03; E203.F5.T02, ADR-0146).
 *
 * Até a E203.F5 eram as "áreas de preparação" (protocolo, cadastro, apoio e envio), recolhidas no
 * fim da mesa. O cadastro saiu (o painel do documento o abre no dado), e o protocolo e a tabela de
 * documentos viraram "Protocolo e documentos", fora da tela por padrão: só aparece quando o "Mais"
 * do topo (ou o próximo passo "verificar") pede, e fecha pelo próprio botão. O card do protocolo vem
 * sem "Verificar": salvar já verifica de novo (E180.F3), e "Atualizar fontes externas" fica, com o
 * aviso de custo. O envio fica no alto da aba Documentos, só quando pedido (E207.F6.T02): abre sozinho
 * no caso sem documento, pelo "Enviar documentos" do cabeçalho, pela linha do kit incompleto e pela do
 * arquivo único, e fecha pelo próprio botão; sem o resumo "n documentos recebidos", que a lista já diz.
 */
import { forwardRef, type ReactNode } from 'react';

import { AdicionarDocumentos } from '@/components/ExameEletronico/AdicionarDocumentos';
import { ProtocoloDoCasoCard } from '@/components/ExameEletronico/ProtocoloDoCasoCard';
import type { ProtocoloFormAcao } from '@/components/ExameEletronico/ProtocoloForm';
import { tituloDaSecao } from '@/components/ExameEletronico/SecaoRecolhida';
import { SecaoDaMesa } from '@/components/MesaUnica/SecaoDaMesa';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCasoData, ProtocoloAssinaturaData } from '@/lib/exame_eletronico';
import { useSugestaoPorImagem } from '@/pages/useSugestaoPorImagem';

const td = ptBR.documentosDoCaso.envio;
const tm = ptBR.mesaUnica;

export type SecaoDePreparacao = 'documentos';
export type SecoesAbertas = Record<SecaoDePreparacao, boolean>;

/** O que abre na chegada: o envio, quando ainda não há documento. */
export function secoesIniciais(caso: ExameEletronicoCasoData): SecoesAbertas {
  return { documentos: caso.pecas.length === 0 };
}

const TODAS_RECOLHIDAS: SecoesAbertas = { documentos: false };

export interface ProtocoloEDocumentosProps {
  caseId: string;
  caso: ExameEletronicoCasoData;
  acaoDoCaso: ProtocoloFormAcao | null;
  algumaConcluida: boolean;
  edicaoPendenteDePecas: boolean;
  onSalvarProtocolo: (protocolo: ProtocoloAssinaturaData) => void;
  onAtualizarConsultas: () => void;
  onFechar: () => void;
  /** A tabela de documentos: o código declarado e o nome de cada um. */
  children: ReactNode;
}

/** "Protocolo e documentos": o protocolo do caso e a tabela, quando o "Mais" pede. */
export function ProtocoloEDocumentos({
  caseId,
  caso,
  acaoDoCaso,
  algumaConcluida,
  edicaoPendenteDePecas,
  onSalvarProtocolo,
  onAtualizarConsultas,
  onFechar,
  children,
}: ProtocoloEDocumentosProps): JSX.Element {
  const porImagem = useSugestaoPorImagem(caseId, caso);
  return (
    <SecaoDaMesa id="protocolo" titulo={tm.acoes.protocoloEDocumentos}>
      <div className="mb-sc-3 flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onFechar}
          data-testid="protocolo-e-documentos-fechar"
        >
          {tm.protocoloEDocumentos.fechar}
        </Button>
      </div>
      <ProtocoloDoCasoCard
        protocolo={caso.protocolo}
        totalDePecas={caso.pecas.length}
        acaoEmCurso={acaoDoCaso}
        concluido={algumaConcluida}
        onSalvar={onSalvarProtocolo}
        onAtualizarConsultas={onAtualizarConsultas}
        {...(caso.formato_da_data_do_catalogo == null
          ? {}
          : { formatoDoCatalogo: caso.formato_da_data_do_catalogo })}
        edicaoPendenteDePecas={edicaoPendenteDePecas}
        sugestaoPorImagem={porImagem.sugestao}
        leituraEmAndamento={porImagem.lendo}
      />
      {children}
    </SecaoDaMesa>
  );
}

export interface EnvioDeDocumentosProps {
  caseId: string;
  algumaConcluida: boolean;
  abertas: SecoesAbertas | null;
  onAlternar: (secao: SecaoDePreparacao) => void;
  onDocumentoAdicionado: () => void;
}

/**
 * E205.F4.T04 (ADR-0147 §2.5): "Enviar documentos", no alto da aba Documentos. O `ref` aponta para
 * o envio: o botão do cabeçalho, a linha do kit incompleto e a do arquivo único o abrem e rolam até
 * ele. O apoio saiu daqui: a leitura de cada documento de apoio se confirma na linha dele.
 *
 * E207.F6.T02 (owner, 01/11): o bloco só existe aberto. Recolhido, nada aparece (o cabeçalho já tem
 * o botão e a lista já conta os documentos); o `div` fica para a âncora e a rolagem.
 */
export const EnvioDeDocumentos = forwardRef<HTMLDivElement, EnvioDeDocumentosProps>(
  function EnvioDeDocumentos(
    { caseId, algumaConcluida, abertas, onAlternar, onDocumentoAdicionado },
    refDocumentos,
  ): JSX.Element {
    const aberta = (abertas ?? TODAS_RECOLHIDAS).documentos;
    const tituloId = tituloDaSecao('documentos');
    return (
      <div
        ref={refDocumentos}
        id="adicionar-documentos"
        className="scroll-mt-sc-6"
        data-testid="envio-de-documentos"
      >
        {aberta ? (
          <section
            className="mb-sc-4 border border-border"
            data-testid="secao-documentos"
            data-aberta="true"
            aria-labelledby={tituloId}
          >
            <div className="flex flex-wrap items-start justify-between gap-sc-3 px-sc-4 py-sc-3">
              <div className="min-w-0">
                <h2 id={tituloId} className="text-body-md font-semibold text-fg">
                  {td.titulo}
                </h2>
                <p className="mt-0.5 text-body-sm text-fg-muted">{td.texto}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onAlternar('documentos')}
                data-testid="secao-documentos-alternar"
              >
                {td.fechar}
              </Button>
            </div>
            <div className="border-t border-border p-sc-4">
              {/* E84.F1: o caso segue aceitando documento e comprovante depois do cadastro. */}
              <AdicionarDocumentos
                caseId={caseId}
                concluido={algumaConcluida}
                onAdicionado={onDocumentoAdicionado}
                tituloId={tituloId}
              />
            </div>
          </section>
        ) : null}
      </div>
    );
  },
);
